import type { ButtonVariants } from '~/components/ui/button';
import type { CarouselGenericActionVariants } from '~/components/ui/carousel/CarouselGenericAction';
import type { CarouselItemSelectorProps } from '~/components/ui/carousel/CarouselItemSelector';

import { cva } from 'class-variance-authority';

export type ShortsSectionVariant = 'dark' | 'light';

interface VariantConfig {
	titleText: string;
	/** Optional gap class for carousel content (Section) */
	contentGap: string | undefined;
	carouselContentClass: string;
	selectorVariant: CarouselItemSelectorProps['variant'];
	selectorArrowsVariant: CarouselGenericActionVariants['variant'];
	showCtaButton: boolean;
	buttonVariant: ButtonVariants['variant'];
	ctaArrowClass: string;
}

const sectionVariants = cva('w-full', {
	variants: {
		variant: {
			dark: 'bg-primary py-12 md:py-16',
			light: 'border-b border-white-dark-3'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const containerVariants = cva('', {
	variants: {
		variant: {
			dark: 'bg-transparent',
			light:
				'px-0 md:px-0 lg:px-0 pt-[44px] pb-3 md:pt-6 md:pb-6 lg:pt-[44px] lg:pb-[44px]'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const headerVariants = cva('flex flex-col items-start gap-6', {
	variants: {
		variant: {
			dark: 'pb-6 md:pb-9',
			light: 'pb-4 lg:pb-6 px-5 md:px-0'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const titleFontVariants = cva('leading-[1.5]', {
	variants: {
		variant: {
			dark: 'font-matter-sq text-2xl font-normal tracking-tight md:text-4xl',
			light: 'text-xl font-medium md:text-2xl md:font-normal lg:font-medium'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const carouselItemVariants = cva('basis-[94%] md:basis-[48%]', {
	variants: {
		variant: {
			dark: 'lg:basis-[32%] xl:basis-[24%]',
			light: 'xl:basis-[32%]'
		}
	},
	defaultVariants: { variant: 'dark' }
});

export const placeholderVariants = cva('animate-pulse', {
	variants: {
		variant: {
			dark: 'bg-primary-light-4',
			light: 'bg-white-dark-2'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const selectorContainerVariants = cva(
	'flex w-full justify-end gap-2 lg:gap-3',
	{
		variants: {
			variant: {
				dark: 'md:max-w-[420px]',
				light: ''
			}
		},
		defaultVariants: { variant: 'dark' }
	}
);

const selectorBarVariants = cva('h-[2px] max-w-[60px] flex-1', {
	variants: {
		variant: {
			dark: 'bg-white/20',
			light: 'bg-white-dark-2'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const arrowContainerVariants = cva('flex max-w-[66px] gap-1 lg:gap-2', {
	variants: {
		variant: {
			dark: 'text-white/20',
			light: 'text-white-dark-2'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const loadingBarVariants = cva('h-[2px] w-full overflow-hidden', {
	variants: {
		variant: {
			dark: 'block',
			light: 'hidden'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const loadingBarInnerVariants = cva('h-full w-1/3 animate-pulse', {
	variants: {
		variant: {
			dark: 'bg-white/30',
			light: ''
		}
	},
	defaultVariants: { variant: 'dark' }
});

const skeletonContentGapVariants = cva('flex w-full flex-col', {
	variants: {
		variant: {
			dark: 'gap-6 md:gap-9',
			light: 'gap-4 lg:gap-7'
		}
	},
	defaultVariants: { variant: 'dark' }
});

const variantConfig: Record<ShortsSectionVariant, VariantConfig> = {
	dark: {
		titleText: 'text-white',
		contentGap: undefined,
		carouselContentClass: 'gap-5',
		selectorVariant: 'white',
		selectorArrowsVariant: 'default',
		showCtaButton: true,
		buttonVariant: 'primary-dark',
		ctaArrowClass: 'text-primary'
	},
	light: {
		titleText: 'text-primary',
		contentGap: 'gap-4 lg:gap-7',
		carouselContentClass: 'gap-5 pl-5 md:pl-0',
		selectorVariant: 'dark',
		selectorArrowsVariant: 'dark',
		showCtaButton: false,
		buttonVariant: 'primary',
		ctaArrowClass: 'text-white'
	}
};

export function getSectionStyles(variant: ShortsSectionVariant) {
	return {
		section: sectionVariants({ variant }),
		containerClass: containerVariants({ variant }),
		headerClass: headerVariants({ variant }),
		titleFont: titleFontVariants({ variant }),
		carouselItemClass: carouselItemVariants({ variant }),
		...variantConfig[variant]
	};
}

export function getSkeletonStyles(variant: ShortsSectionVariant) {
	return {
		section: sectionVariants({ variant }),
		containerClass: containerVariants({ variant }),
		headerClass: headerVariants({ variant }),
		titleFont: titleFontVariants({ variant }),
		carouselItemClass: carouselItemVariants({ variant }),
		contentGap: skeletonContentGapVariants({ variant }),
		placeholder: placeholderVariants({ variant }),
		selectorContainer: selectorContainerVariants({ variant }),
		selectorBar: selectorBarVariants({ variant }),
		arrowContainer: arrowContainerVariants({ variant }),
		loadingBar: loadingBarVariants({ variant }),
		loadingBarInner: loadingBarInnerVariants({ variant }),
		showCtaButton: variantConfig[variant].showCtaButton
	};
}
